// src/pages/ParticipantsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useProgram } from '../context/ProgramContext';
import apiClient from '../api';
import '../styles/ParticipantsPage.css';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Intl.DateTimeFormat('en-US', options).format(new Date(dateString));
    } catch (e) {
        return dateString;
    }
};

const truncateAddr = (addr) => addr ? `${addr.substring(0, 6)}…${addr.substring(addr.length - 4)}` : '-';

const ParticipantsPage = ({ isAuthenticated }) => {
    const { selectedProgram } = useProgram();
    const [participants, setParticipants] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [busyRedemptionId, setBusyRedemptionId] = useState(null);
    const [actionMsg, setActionMsg] = useState('');

    const fetchParticipants = useCallback(async () => {
        if (!selectedProgram) { setParticipants([]); return; }
        setIsLoading(true); setError('');
        try {
            const response = await apiClient.get(`/programs/${selectedProgram.id}/participants/with-redemptions`);
            setParticipants(response.data || []);
        } catch (err) {
            const errorMsg = err.response?.data?.detail || err.message || 'Failed to load participants.';
            setError(errorMsg);
            setParticipants([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedProgram]);

    useEffect(() => {
        if (isAuthenticated && selectedProgram) fetchParticipants();
        else { setParticipants([]); setIsLoading(false); setError(''); }
    }, [isAuthenticated, selectedProgram, fetchParticipants]);

    const handleFulfill = async (redemptionId) => {
        setActionMsg(''); setBusyRedemptionId(redemptionId);
        try {
            await apiClient.post(`/redemptions/${redemptionId}/fulfill`);
            setActionMsg('Redemption marked as fulfilled.');
            await fetchParticipants();
        } catch (err) {
            setActionMsg(`Error: ${err.response?.data?.detail || err.message || 'Fulfill failed.'}`);
        } finally {
            setBusyRedemptionId(null);
        }
    };

    const handleCancel = async (redemptionId) => {
        if (!window.confirm('Cancel this redemption? For off-chain programs, the points will be refunded to the participant. For on-chain programs, you must refund the tokens manually.')) return;
        setActionMsg(''); setBusyRedemptionId(redemptionId);
        try {
            await apiClient.post(`/redemptions/${redemptionId}/cancel`);
            setActionMsg('Redemption cancelled.');
            await fetchParticipants();
        } catch (err) {
            setActionMsg(`Error: ${err.response?.data?.detail || err.message || 'Cancel failed.'}`);
        } finally {
            setBusyRedemptionId(null);
        }
    };

    if (!isAuthenticated) return <Navigate to="/dashboard" replace />;

    if (!selectedProgram) {
        return (
            <div className="participants-container info-message">
                <h2>No Program Selected</h2>
                <p>Please <Link to="/select-program">select a program</Link> first to view its participants.</p>
            </div>
        );
    }

    return (
        <div className="participants-container">
            <h2>Participants</h2>
            <p>View participants enrolled in the <strong>{selectedProgram.name}</strong> ({selectedProgram.short_name}) program.</p>

            {actionMsg && <p className={actionMsg.startsWith('Error') ? 'error-message' : 'success-message'}>{actionMsg}</p>}
            {isLoading && <p className="loading-message">Loading participants...</p>}
            {error && <p className="error-message">Error: {error}</p>}

            {!isLoading && !error && (
                <div className="table-container">
                    {participants.length === 0 ? (
                        <p className="info-message">No participants found for this program yet.</p>
                    ) : (
                        <table>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Identifier</th>
                                    <th>Username</th>
                                    <th>Email</th>
                                    <th>Points</th>
                                    <th>Algo Address</th>
                                    <th>Registered</th>
                                    <th>Pending Redemptions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {participants.map(p => {
                                    const addr = p.algorand_address || p.custodial_address || p.pera_address;
                                    const pending = p.pending_redemptions || [];
                                    return (
                                        <tr key={p.id}>
                                            <td>{p.id}</td>
                                            <td>{p.user_identifier}</td>
                                            <td>{p.username || '-'}</td>
                                            <td>{p.email || '-'}</td>
                                            <td>{p.points_balance}</td>
                                            <td title={addr || ''}>{truncateAddr(addr)}</td>
                                            <td>{formatDate(p.registration_date)}</td>
                                            <td>
                                                {pending.length === 0 ? (
                                                    <span className="muted">—</span>
                                                ) : (
                                                    <ul className="pending-list">
                                                        {pending.map(r => (
                                                            <li key={r.redemption_id}>
                                                                <span className="pending-title">{r.reward_title}</span>
                                                                <span className="pending-actions">
                                                                    <button
                                                                        className="link-btn"
                                                                        disabled={busyRedemptionId === r.redemption_id}
                                                                        onClick={() => handleFulfill(r.redemption_id)}
                                                                    >
                                                                        {busyRedemptionId === r.redemption_id ? '…' : 'Fulfill'}
                                                                    </button>
                                                                    <button
                                                                        className="link-btn danger"
                                                                        disabled={busyRedemptionId === r.redemption_id}
                                                                        onClick={() => handleCancel(r.redemption_id)}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};

export default ParticipantsPage;
